import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ShieldAlert, Flame, MessageSquareReply, Lock } from "lucide-react";
import Layout from "@/components/Layout";
import { ResultView } from "@/components/Results";
import { api, vibrate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const VERTICALS = [
  { id: "red_flags", label: "Red Flags", icon: ShieldAlert },
  { id: "rizz_score", label: "Rizz Score", icon: Flame },
  { id: "reply_suggestions", label: "Réponses", icon: MessageSquareReply },
];

const SAMPLE = `moi: salut ça va ? t'as passé un bon week-end ?
lui: ouais tranquille et toi
moi: cool ! on se voit quand du coup ?
lui: jsp on verra
moi: ok pas de souci, dis moi quand t'es dispo
lui: t'es trop dispo toi non ? faut te calmer un peu`;

export default function Analyze() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [vertical, setVertical] = useState("red_flags");
  const [text, setText] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [paywall, setPaywall] = useState(false);

  const run = async () => {
    if (!text.trim()) {
      toast.error("Colle d'abord une conversation 😉");
      return;
    }
    vibrate();
    setLoading(true);
    setResult(null);
    setPaywall(false);
    try {
      const res = await api.post("/analyze", {
        raw_text: text,
        vertical,
        source: "paste",
        contact_name: contact || null,
      });
      vibrate([100, 50, 100]);
      setResult({ vertical: res.data.vertical, data: res.data.result });
      setRemaining(res.data.free_analyses_remaining);
      checkAuth();
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error("L'analyse a échoué, réessaie.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-5">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Nouvelle analyse</h1>
          <p className="text-zinc-500 text-sm mt-1">Colle ta conv, choisis un mode, lance le Doc.</p>
        </div>

        <div className="flex gap-2">
          {VERTICALS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`vertical-${id}`}
              onClick={() => { vibrate(); setVertical(id); }}
              className={`flex-1 flex flex-col items-center gap-1 rounded-xl py-3 border transition-all ${
                vertical === id
                  ? "border-pink-500 bg-pink-500/10 text-pink-400 glow-pink"
                  : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
              }`}
            >
              <Icon size={18} />
              <span className="text-xs font-semibold">{label}</span>
            </button>
          ))}
        </div>

        <input
          data-testid="contact-name-input"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="Prénom du contact (optionnel)"
          className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:border-purple-500 focus:outline-none transition-colors"
        />

        <div className="neon-border rounded-xl">
          <textarea
            data-testid="paste-chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"Colle ta conversation ici…\nEx: moi: coucou\nlui: salut"}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-sm text-white focus:outline-none resize-none min-h-[160px]"
          />
        </div>

        <div className="flex items-center justify-between">
          <button
            data-testid="load-sample"
            onClick={() => { setText(SAMPLE); toast("Exemple chargé"); }}
            className="text-xs text-zinc-500 hover:text-pink-400 transition-colors"
          >
            Charger un exemple
          </button>
          {remaining !== null && (
            <span data-testid="free-counter" className="text-xs text-zinc-500">
              {remaining} analyse{remaining > 1 ? "s" : ""} gratuite{remaining > 1 ? "s" : ""} restante{remaining > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <button
          data-testid="analyze-button"
          onClick={run}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 active:scale-95 transition-transform hover:bg-pink-600 glow-pink disabled:opacity-60"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              Le Doc analyse…
            </>
          ) : (
            <>
              <Sparkles size={18} /> Analyser
            </>
          )}
        </button>

        {paywall && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="neon-border rounded-2xl bg-zinc-900 p-6 text-center"
          >
            <Lock className="mx-auto text-pink-400 mb-3" size={28} />
            <p className="font-display text-xl font-bold">Tes 3 analyses gratuites sont finies</p>
            <p className="text-sm text-zinc-400 mt-2">Passe Premium pour des analyses illimitées.</p>
            <button
              data-testid="paywall-cta"
              onClick={() => { vibrate(); navigate("/pricing"); }}
              className="mt-4 w-full rounded-full bg-pink-500 text-white font-bold py-3 active:scale-95 transition-transform hover:bg-pink-600"
            >
              Voir les offres Premium
            </button>
          </motion.div>
        )}

        {result && (
          <div data-testid="analysis-result" className="pt-2">
            <ResultView vertical={result.vertical} result={result.data} />
          </div>
        )}
      </div>
    </Layout>
  );
}
